'use client'
import { useEffect, useRef } from 'react'
import type { FormProvider } from '@riksja/common/form-embed'
import { createWidget } from '@typeform/embed'
import { stegaClean } from 'next-sanity'

import type { AcfFormEmbedProps } from '@typings'
import { RaContainer, RaHeading, RaIllustration, RaPaper } from '@components/core/index.core'

import '@typeform/embed/build/css/widget.css'
import styles from './acf-form-embed.module.scss'

// The embed url carries the Dotdigital page id as its `id` query param.
const getPageId = (embedUrl: string): string | null => {
  try {
    return new URL(embedUrl).searchParams.get('id')
  } catch {
    return null
  }
}

const DotdigitalForm = ({ embedUrl }: { embedUrl: string }) => {
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const container = containerRef.current
    if (!container || !embedUrl) return

    // Dotdigital's embed.js skips any page id already in `window.__dmProcessedPages`, and
    // that list only resets on a full document load. Without dropping our id, re-running
    // the script after a client-side navigation back to this page renders nothing.
    //
    // Dotdigital support confirmed (August 2026) that this is current behaviour and that a
    // full browser refresh is the only way they support re-rendering an embed; they offered
    // to raise a feature request (roadmap: https://r1.dotdigital-pages.com/p/35E-386/dotdigital-roadmap).
    // So resetting the flag ourselves is deliberate, but it leans on an undocumented internal
    // of embed.js and can break without warning on their side.
    const pageId = getPageId(embedUrl)
    if (pageId && Array.isArray(window.__dmProcessedPages)) {
      window.__dmProcessedPages = window.__dmProcessedPages.filter(id => id !== pageId)
    }

    const script = document.createElement('script')
    script.src = embedUrl
    script.async = true
    container.appendChild(script)

    return () => {
      // The Dotdigital script injects the form next to its own tag;
      // clearing the container removes both on unmount or url change.
      container.replaceChildren()
    }
  }, [embedUrl])

  return <div ref={containerRef} />
}

const TypeformForm = ({ embedUrl, title }: { embedUrl: string; title: string }) => {
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const container = containerRef.current
    if (!container || !embedUrl) return

    // Passing the full form URL (not just the id) keeps the iframe on riksjatravel.typeform.com,
    // the host frame-src allows (see apps/web/src/csp.ts); the SDK otherwise uses form.typeform.com.
    const widget = createWidget(embedUrl, {
      container,
      // Grow with the current question instead of scrolling inside a fixed-height frame.
      autoResize: true,
      // Forward the page's query string (utm_*, gclid, …) so responses can be attributed.
      transitiveSearchParams: true,
      // Render inline on mobile instead of behind a "start" placeholder that opens fullscreen.
      inlineOnMobile: true,
      lazy: true,
      iframeProps: { title },
    })

    return () => widget.unmount()
  }, [embedUrl, title])

  return (
    <div
      ref={containerRef}
      className={styles['typeform-embed']}
    />
  )
}

const renderForm = (provider: FormProvider, embedUrl: string, title: string) => {
  switch (provider) {
    case 'typeform':
      return (
        <TypeformForm
          embedUrl={embedUrl}
          title={title}
        />
      )
    case 'dotdigital':
      return <DotdigitalForm embedUrl={embedUrl} />
  }
}

export const AcfFormEmbed = ({ provider, heading, embedUrl }: AcfFormEmbedProps) => {
  const url = stegaClean(embedUrl)

  return (
    <RaContainer>
      <RaIllustration index={2} />
      {heading && <RaHeading component="h3">{heading}</RaHeading>}
      <RaPaper
        padding={4}
        paddingMobile={4}>
        {renderForm(provider, url, heading || 'Form')}
      </RaPaper>
    </RaContainer>
  )
}
